import React from 'react';
import {useSearchParams} from 'react-router-dom';
import {ArrowLeftIcon} from '@heroicons/react/24/outline';
import {Transitioner, ButtonTheme} from 'components';
import {useLeavePage} from 'hooks';
import {classNames} from 'utils';

import {NAVIGATIONS} from '../Page.Projects.Update';
import useProjectStore from '../stores/useProjectStore';

interface ISidebarEdgeProps {
  workspace: string | undefined;
  resource: string | undefined;
  onChangeParams: ({nav}: {nav: string}) => void;
}

const SidebarEdge = ({workspace, resource, onChangeParams}: ISidebarEdgeProps) => {
  const [searchParams] = useSearchParams();
  const nav = searchParams.get('nav');

  const isDirty = useProjectStore(s => s.isEditorDirty || s.isMeshesDirty);
  const {handleManualLeave} = useLeavePage({
    leaveUrl: `/${workspace}/${resource}`,
    canTrigger: isDirty,
  });

  return (
    <section className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-20 lg:overflow-y-auto lg:pb-4 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-950 flex flex-col items-center justify-between">
      <Transitioner fromX={-50} fromY={0}>
        <div>
          <div className="flex h-16 shrink-0 items-center justify-center">
            <button
              type="button"
              className="bg-slate-200 dark:bg-slate-700 hover:brightness-105 dark:hover:brightness-130 size-10 rounded-full flex justify-center items-center active:scale-90 duration-300 cursor-pointer"
              onClick={handleManualLeave}>
              <ArrowLeftIcon className="size-5 text-slate-700 dark:text-white" />
            </button>
          </div>
          <nav className="mt-2.5">
            <ul role="list" className="flex flex-col items-center space-y-1">
              {Object.entries(NAVIGATIONS).map(([key, item]) => {
                const isNavValid = NAVIGATIONS[nav as keyof typeof NAVIGATIONS];
                const current = key === (isNavValid ? nav : Object.keys(NAVIGATIONS)[0]);
                return <NavItem key={item.name} current={current} nav={key} item={item} onClick={onChangeParams} />;
              })}
            </ul>
          </nav>
        </div>
        <div className="flex flex-col items-center space-y-1 mt-1.5">
          <ButtonTheme />
        </div>
      </Transitioner>
    </section>
  );
};

export default React.memo(SidebarEdge);

interface INavItemProps {
  current: boolean;
  nav: string;
  item: (typeof NAVIGATIONS)[keyof typeof NAVIGATIONS];
  onClick: ({nav}: {nav: string}) => void;
}

const NavItem = ({current, nav, item, onClick}: INavItemProps) => {
  return (
    <li>
      <button
        type="button"
        onClick={() => onClick({nav})}
        className={classNames(
          current ? 'bg-primary text-white' : 'text-slate-500 hover:bg-primary hover:text-white',
          'group flex gap-x-3 rounded-md p-3 text-sm/6 font-semibold cursor-pointer',
        )}>
        <item.icon aria-hidden="true" className="size-6 shrink-0" />
        <span className="sr-only">{item.name}</span>
      </button>
    </li>
  );
};
